/**
 * WorkbookRenderer (Feb 2026 · final-interaction closeout).
 *
 * Additions vs previous version:
 *   • TRUE frozen first column — every body/header cell in column 0 has an
 *     opaque background layer beneath any highlight tint, correct sticky/
 *     z-index stack, so numeric columns scroll BEHIND the row-label column
 *     with zero bleed-through.
 *   • Canonical selection model —
 *       selection = {
 *         ranges: [{r1,c1,r2,c2}],      contiguous rectangular ranges
 *         cells:  Set<"R{r}C{c}">,       discontiguous individual cells
 *         cols:   Set<number>,           whole-column selections
 *         anchor: {r,c} | {kind:'col',c},last click for Shift-extend
 *         focus:  {r,c},                 last click / active cursor
 *       }
 *     Same object drives Highlight, Bold, Colour Scale, Clear Fill.
 *   • Discontiguous multi-select via Ctrl/Cmd + click / drag.
 *   • Shift + click extends most-recent range from its anchor.
 *   • Column-header click selects entire column; Shift/Ctrl variants supported.
 *   • Column resize handles preserved; auto-fit via double-click preserved.
 *   • Source overrides & added row/col editing preserved.
 *   • Manual highlight visually overrides colour-scale rule (rule preserved
 *     underneath — clearing manual highlight reveals the scale automatically).
 */
import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { formatCellDisplay } from './formatCell';

// ──────────────────────────────────────────────────────────────────────
// Constants + helpers
// ──────────────────────────────────────────────────────────────────────
const VIRT_ROW_THRESHOLD = 500;
const VIRT_CELL_THRESHOLD = 20000;
const ROW_HEIGHT_DEFAULT = 30;
const OVERSCAN = 8;
const REMARKS_COL_WIDTH = 260;
const MIN_COL_WIDTH = 70;
const MAX_COL_WIDTH = 480;
const DEFAULT_NUMERIC_WIDTH = 148;
const DEFAULT_LABEL_WIDTH = 220;

// Light-shade palette — legible black text on top.
export const HIGHLIGHT_BG = {
  green:       '#D9F0DF',
  amber:       '#FDECC8',
  red:         '#FADAD6',
  yellow:      '#FFF6C2',
  lightgreen:  '#E4F5D1',
  mint:        '#D4F3EA',
  lightblue:   '#DCEEFB',
  blue:        '#C6E2F9',
  lavender:    '#E5DDF7',
  pink:        '#FBDCEB',
  orange:      '#FEE0C7',
  grey:        '#E5E9E5',
};

const cellKey  = (r, c) => `R${r}C${c}`;
const rowKey   = (r)    => `R${r}`;
const colKey   = (c)    => `C${c}`;

function _resolvePosition(position) {
  if (!position) return { kind: 'end' };
  const m = /^(after|before):(R|C)(\d+)$/.exec(position);
  if (!m) return { kind: 'end' };
  return { kind: m[1], axis: m[2], index: parseInt(m[3], 10) };
}

function _computeColumnOrder(sourceCols, addedCols) {
  const list = sourceCols.map(c => ({
    kind: 'source', ref: colKey(c.index), sourceIndex: c.index, hidden: c.hidden,
    letter: c.letter, defaultWidth: c.width,
  }));
  for (const a of addedCols) {
    const pos = _resolvePosition(a.position);
    const entry = { kind: 'added', ref: `AC:${a.id}`, addedId: a.id, title: a.title };
    if (pos.kind === 'end' || pos.axis !== 'C') { list.push(entry); continue; }
    const idxAt = list.findIndex(x => x.kind === 'source' && x.sourceIndex === pos.index);
    if (idxAt < 0) list.push(entry);
    else if (pos.kind === 'after') list.splice(idxAt + 1, 0, entry);
    else list.splice(idxAt, 0, entry);
  }
  return list;
}

function _computeRowOrder(totalSourceRows, rowMetaByIdx, addedRows) {
  const list = [];
  for (let i = 1; i <= totalSourceRows; i++) {
    const rm = rowMetaByIdx.get(i);
    list.push({ kind: 'source', ref: rowKey(i), sourceIndex: i,
                 height: (rm?.hidden ? 0 : (rm?.height ? Math.max(20, Math.min(60, rm.height * 1.2)) : null)) });
  }
  for (const a of addedRows) {
    const pos = _resolvePosition(a.position);
    const entry = { kind: 'added', ref: `AR:${a.id}`, addedId: a.id };
    if (pos.kind === 'end' || pos.axis !== 'R') { list.push(entry); continue; }
    const idxAt = list.findIndex(x => x.kind === 'source' && x.sourceIndex === pos.index);
    if (idxAt < 0) list.push(entry);
    else if (pos.kind === 'after') list.splice(idxAt + 1, 0, entry);
    else list.splice(idxAt, 0, entry);
  }
  return list;
}

function _indexAnnotations(anns) {
  const rowRemark   = new Map();
  const rowStyle    = new Map();
  const colStyle    = new Map();
  const cellStyle   = new Map();
  const rangeCells  = [];
  const colWidths   = new Map();
  const addedRows   = [];
  const addedCols   = [];
  const addedValues = new Map();
  const sourceOverrides = new Map();  // "R{n}C{n}" → { value, id }
  for (const a of anns) {
    switch (a.target_type) {
      case 'row':
        if (a.remark) rowRemark.set(a.target_ref, a);
        if (a.highlight || a.custom_fill || a.bold != null) {
          const cur = rowStyle.get(a.target_ref) || {};
          rowStyle.set(a.target_ref, {
            hl: a.highlight ?? cur.hl, custom: a.custom_fill ?? cur.custom,
            bold: a.bold ?? cur.bold, id: a.id,
          });
        }
        break;
      case 'column':
        if (a.highlight || a.custom_fill || a.bold != null)
          colStyle.set(a.target_ref, {
            hl: a.highlight, custom: a.custom_fill, bold: a.bold, id: a.id });
        break;
      case 'cell':
        if (a.highlight || a.custom_fill || a.bold != null) {
          const cur = cellStyle.get(a.target_ref) || {};
          cellStyle.set(a.target_ref, {
            hl: a.highlight ?? cur.hl, custom: a.custom_fill ?? cur.custom,
            bold: a.bold ?? cur.bold, id: a.id });
        }
        break;
      case 'range': {
        const m = /^R(\d+)C(\d+):R(\d+)C(\d+)$/.exec(a.target_ref || '');
        if (m) rangeCells.push({
          r1: +m[1], c1: +m[2], r2: +m[3], c2: +m[4],
          hl: a.highlight, custom: a.custom_fill, bold: a.bold, scale: a.color_scale, id: a.id });
        break;
      }
      case 'column_width':
        if (typeof a.width === 'number') colWidths.set(a.target_ref, a.width);
        break;
      case 'added_row':
        addedRows.push({ id: a.id, position: a.position });
        break;
      case 'added_column':
        addedCols.push({ id: a.id, title: a.title, position: a.position });
        break;
      case 'added_cell':
        addedValues.set(a.target_ref, { value: a.value, id: a.id });
        break;
      case 'source_override':
        sourceOverrides.set(a.target_ref, { value: a.value, id: a.id });
        break;
      default: break;
    }
  }
  return { rowRemark, rowStyle, colStyle, cellStyle, rangeCells, colWidths,
           addedRows, addedCols, addedValues, sourceOverrides };
}

// ──────────────────────────────────────────────────────────────────────
// Canonical selection helpers (exported for parent + tests)
// ──────────────────────────────────────────────────────────────────────
export const EMPTY_SELECTION = { ranges: [], cells: [], cols: [], anchor: null, focus: null };

export function selectionHasCell(sel, r, c) {
  if (!sel) return false;
  if (sel.cols?.length && sel.cols.includes(c)) return true;
  if (sel.cells?.length && sel.cells.includes(cellKey(r, c))) return true;
  for (const rng of (sel.ranges || [])) {
    if (r >= rng.r1 && r <= rng.r2 && c >= rng.c1 && c <= rng.c2) return true;
  }
  return false;
}

function _rangesEqual(a, b) {
  return a.r1 === b.r1 && a.c1 === b.c1 && a.r2 === b.r2 && a.c2 === b.c2;
}

// ──────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────
export function WorkbookRenderer({
  sheet,
  styles = [],
  annotations = [],
  selection,                // canonical: {ranges,cells,cols,anchor,focus}
  onSelectionChange,
  onRemarkCommit,
  onCellValueCommit,
  onSourceOverrideCommit,
  onColumnWidthCommit,
  showRemarks = true,
  compact = false,
  editable = true,
  presentation = false,
  // ── Quick-Calc extension columns (sprint 375) ──
  calcColumns = 0,          // N extra scratch columns rendered at right
  calcCells = {},           // {"R{r}C{cIdx}": {formula, fmt}}  cIdx is 0..N-1
  activeCalcCell = null,    // {r, c} where c is 0..N-1
  calcDraft = '',
  onCalcActivate,           // (r, c) => void
  onCalcFormulaChange,      // (draft) => void
  onCalcCommit,             // (r, c, formula) => void
  onWorkbookCellClickForFormula, // (r, c) => bool consumed
}) {
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(600);

  const sheetIdx = sheet.sheet_index;
  const sheetAnn = useMemo(
    () => (annotations || []).filter(a => a.sheet_index === sheetIdx),
    [annotations, sheetIdx],
  );
  const idx = useMemo(() => _indexAnnotations(sheetAnn), [sheetAnn]);

  const sourceRows = sheet?.dims?.rows || 0;
  const rowMetaByIdx = useMemo(() => {
    const m = new Map();
    for (const rm of (sheet?.row_meta || [])) m.set(rm.index, rm);
    return m;
  }, [sheet]);

  const columnOrder = useMemo(() => {
    const base = _computeColumnOrder(sheet?.columns || [], idx.addedCols);
    if (calcColumns > 0) {
      for (let i = 0; i < calcColumns; i++) {
        base.push({ kind: 'calc', ref: `QC${i}`, calcIndex: i, title: `Calc ${String.fromCharCode(65 + i)}` });
      }
    }
    return base;
  }, [sheet, idx.addedCols, calcColumns]);
  const rowOrder = useMemo(
    () => _computeRowOrder(sourceRows, rowMetaByIdx, idx.addedRows),
    [sourceRows, rowMetaByIdx, idx.addedRows],
  );

  const cellByRC = useMemo(() => {
    const m = new Map();
    for (const c of (sheet?.cells || [])) m.set(cellKey(c.r, c.c), c);
    return m;
  }, [sheet]);

  const mergedCoverage = useMemo(() => {
    const covered = new Set();
    const anchors = new Map();
    for (const mr of (sheet?.merged_ranges || [])) {
      const a = cellKey(mr.r1, mr.c1);
      anchors.set(a, { rowspan: mr.r2 - mr.r1 + 1, colspan: mr.c2 - mr.c1 + 1 });
      for (let r = mr.r1; r <= mr.r2; r++)
        for (let c = mr.c1; c <= mr.c2; c++)
          if (!(r === mr.r1 && c === mr.c1)) covered.add(cellKey(r, c));
    }
    return { covered, anchors };
  }, [sheet]);

  const [pendingWidth, setPendingWidth] = useState({});
  const columnWidthOf = (col) => {
    if (pendingWidth[col.ref] != null) return pendingWidth[col.ref];
    const persisted = idx.colWidths.get(col.ref);
    if (persisted) return Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, persisted));
    if (col.kind === 'source') {
      const hint = col.defaultWidth ? col.defaultWidth * 7.5 + 12 : null;
      const isFirstCol = col.sourceIndex === 1;
      const base = isFirstCol ? DEFAULT_LABEL_WIDTH : DEFAULT_NUMERIC_WIDTH;
      const chosen = hint ? Math.max(base, hint) : base;
      return Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, chosen));
    }
    if (col.kind === 'calc') return 140;
    return 200;
  };

  const totalCells = rowOrder.length * columnOrder.length;
  const needsVirt = rowOrder.length > VIRT_ROW_THRESHOLD || totalCells > VIRT_CELL_THRESHOLD;

  const rowHeightOf = (row) => {
    if (row.kind === 'source') return row.height === 0 ? 0 : (row.height || ROW_HEIGHT_DEFAULT);
    return ROW_HEIGHT_DEFAULT + 4;
  };
  const rowOffsets = useMemo(() => {
    const o = [0];
    for (let i = 0; i < rowOrder.length; i++) o[i + 1] = o[i] + rowHeightOf(rowOrder[i]);
    return o;
  }, [rowOrder]);
  const totalHeight = rowOffsets[rowOrder.length] || 0;

  useEffect(() => {
    if (!needsVirt) return;
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    const onResize = () => setViewportH(el.clientHeight);
    el.addEventListener('scroll', onScroll);
    setViewportH(el.clientHeight);
    window.addEventListener('resize', onResize);
    return () => { el.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize); };
  }, [needsVirt]);

  let firstRowIdx = 0, lastRowIdx = rowOrder.length - 1;
  if (needsVirt) {
    let lo = 0, hi = rowOrder.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (rowOffsets[mid + 1] < scrollTop) lo = mid + 1; else hi = mid;
    }
    firstRowIdx = Math.max(0, lo - OVERSCAN);
    let end = firstRowIdx;
    while (end < rowOrder.length && rowOffsets[end + 1] < scrollTop + viewportH + OVERSCAN * ROW_HEIGHT_DEFAULT) end++;
    lastRowIdx = Math.min(rowOrder.length - 1, end + OVERSCAN);
  }

  const rangeHighlightAt = (r, c) => {
    if (r <= 0 || c <= 0) return null;
    for (const rng of idx.rangeCells) {
      if (r >= rng.r1 && r <= rng.r2 && c >= rng.c1 && c <= rng.c2) return rng;
    }
    return null;
  };

  // ── Colour-scale evaluator ──────────────────────────────────────────
  // Each range annotation that carries color_scale produces its own scale
  // pool. For every source cell, we look up the *effective numeric value*
  // (source_override first, otherwise the parsed raw numeric) and interpolate
  // between range min/mid/max. Text/blank/date cells are excluded from the
  // scale pool and receive no scale colour.
  const colorScaleFn = useMemo(() => {
    const rules = idx.rangeCells.filter(r => r.scale);
    if (rules.length === 0) return () => null;
    const compiled = [];
    for (const rule of rules) {
      const nums = [];
      const positions = new Map();  // key → numeric value
      for (let r = rule.r1; r <= rule.r2; r++) {
        for (let c = rule.c1; c <= rule.c2; c++) {
          const key = cellKey(r, c);
          const cell = cellByRC.get(key);
          const override = idx.sourceOverrides.get(key);
          let numeric = null;
          if (override) {
            const parsed = Number(override.value);
            if (Number.isFinite(parsed)) numeric = parsed;
          } else if (cell && typeof cell.raw === 'number' && cell.type !== 'd') {
            numeric = cell.raw;
          }
          if (numeric === null) continue;
          nums.push(numeric);
          positions.set(key, numeric);
        }
      }
      if (nums.length === 0) continue;
      const min = Math.min(...nums);
      const max = Math.max(...nums);
      compiled.push({
        min, max, mid: (min + max) / 2,
        positions,
        palette: rule.scale.palette || 'gyr',
        // sprint 376 · reviewer chooses whether high is good or low is good.
        // 'high_good' (default): low = red, mid = amber, high = green.
        // 'low_good':            low = green, mid = amber, high = red.
        direction: rule.scale.direction === 'low_good' ? 'low_good' : 'high_good',
      });
    }
    return (r, c) => {
      const key = cellKey(r, c);
      for (const rule of compiled) {
        if (!rule.positions.has(key)) continue;
        const v = rule.positions.get(key);
        if (rule.max === rule.min) return _scaleColorAt(0.5, rule.palette, rule.direction);
        const pct = (v - rule.min) / (rule.max - rule.min);
        return _scaleColorAt(pct, rule.palette, rule.direction);
      }
      return null;
    };
  }, [idx.rangeCells, idx.sourceOverrides, cellByRC]);

  // ── Column resize ───────────────────────────────────────────────────
  const dragRef = useRef(null);
  const pendingWidthRef = useRef(pendingWidth);
  useEffect(() => { pendingWidthRef.current = pendingWidth; }, [pendingWidth]);

  const onResizeMouseDown = (col, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = columnWidthOf(col);
    dragRef.current = { colRef: col.ref, startX, startW };
    const onMove = (ev) => {
      const d = ev.clientX - dragRef.current.startX;
      const nw = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, dragRef.current.startW + d));
      setPendingWidth({ [dragRef.current.colRef]: nw });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const final = pendingWidthRef.current[dragRef.current.colRef];
      if (final != null && onColumnWidthCommit) {
        const existing = sheetAnn.find(a => a.target_type === 'column_width' && a.target_ref === dragRef.current.colRef);
        onColumnWidthCommit(dragRef.current.colRef, final, existing?.id);
      }
      setPendingWidth({});
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const canvasRef = useRef(null);
  const autoFitColumn = useCallback((col) => {
    if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
    const ctx = canvasRef.current.getContext('2d');
    ctx.font = `${compact ? 12 : 13}px ui-sans-serif, system-ui`;
    let maxW = 90;
    if (col.kind === 'source') {
      for (const row of rowOrder) {
        if (row.kind !== 'source') continue;
        const c = cellByRC.get(cellKey(row.sourceIndex, col.sourceIndex));
        if (!c) continue;
        const parserStyle = c.style_ref >= 0 ? (styles[c.style_ref] || {}) : {};
        const disp = formatCellDisplay(c.raw, c.type, parserStyle.number_format, c.display);
        maxW = Math.max(maxW, ctx.measureText(disp || '').width + 32);
      }
    }
    if (col.kind === 'added') {
      maxW = Math.max(maxW, ctx.measureText(col.title || 'Column').width + 48);
      for (const [, entry] of idx.addedValues) if (entry) maxW = Math.max(maxW, ctx.measureText(String(entry.value ?? '')).width + 32);
    }
    const w = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, Math.ceil(maxW)));
    const existing = sheetAnn.find(a => a.target_type === 'column_width' && a.target_ref === col.ref);
    onColumnWidthCommit?.(col.ref, w, existing?.id);
  }, [rowOrder, cellByRC, styles, idx.addedValues, sheetAnn, onColumnWidthCommit, compact]);

  // ── Canonical multi-selection engine ────────────────────────────────
  const selectionRef = useRef(selection);
  useEffect(() => { selectionRef.current = selection; }, [selection]);

  const dragStateRef = useRef(null);  // {origin:{r,c}, additive:bool, initial:selection}

  const emit = (next) => { onSelectionChange?.(next); };

  const _normalizeSelection = (s) => {
    const cur = s || EMPTY_SELECTION;
    return {
      ranges: Array.isArray(cur.ranges) ? cur.ranges : [],
      cells:  Array.isArray(cur.cells)  ? cur.cells  : [],
      cols:   Array.isArray(cur.cols)   ? cur.cols   : [],
      anchor: cur.anchor || null,
      focus:  cur.focus  || null,
    };
  };

  const _pruneEmpty = (s) => ({
    ranges: s.ranges.filter(r => r && r.r1 != null),
    cells:  Array.from(new Set(s.cells)),
    cols:   Array.from(new Set(s.cols)),
    anchor: s.anchor,
    focus:  s.focus,
  });

  const beginSelect = (r, c, e) => {
    const shift = e.shiftKey;
    const additive = e.ctrlKey || e.metaKey;
    const cur = _normalizeSelection(selectionRef.current);
    if (shift && cur.anchor && cur.anchor.kind !== 'col') {
      // Replace the *last* range with an anchor-extended box; keep other selection state.
      const box = _boxFrom(cur.anchor, { r, c });
      const next = _pruneEmpty({
        ranges: [box],
        cells:  additive ? cur.cells : [],
        cols:   additive ? cur.cols : [],
        anchor: cur.anchor,
        focus:  { r, c },
      });
      dragStateRef.current = { origin: cur.anchor, additive, initial: next };
      emit(next);
      return;
    }
    if (additive) {
      // Ctrl/Cmd: begin a new range that will be *added* to existing selection.
      const seed = _pruneEmpty({
        ranges: [...cur.ranges, { r1: r, c1: c, r2: r, c2: c }],
        cells:  cur.cells,
        cols:   cur.cols,
        anchor: { r, c },
        focus:  { r, c },
      });
      dragStateRef.current = { origin: { r, c }, additive: true, initial: cur };
      emit(seed);
      return;
    }
    // Plain click → replace selection.
    const seed = {
      ranges: [{ r1: r, c1: c, r2: r, c2: c }],
      cells:  [],
      cols:   [],
      anchor: { r, c },
      focus:  { r, c },
    };
    dragStateRef.current = { origin: { r, c }, additive: false, initial: seed };
    emit(seed);
  };

  const extendSelect = (r, c) => {
    const s = dragStateRef.current;
    if (!s) return;
    const box = _boxFrom(s.origin, { r, c });
    if (s.additive) {
      // Replace only the last range in the initial selection with the extended box.
      const base = _normalizeSelection(s.initial);
      const nextRanges = [...base.ranges, box];
      emit({
        ranges: nextRanges,
        cells:  base.cells,
        cols:   base.cols,
        anchor: s.origin,
        focus:  { r, c },
      });
    } else {
      emit({
        ranges: [box],
        cells:  [],
        cols:   [],
        anchor: s.origin,
        focus:  { r, c },
      });
    }
  };

  const endSelect = () => {
    if (!dragStateRef.current) return;
    // If a single-cell "click" resolved (origin === focus) AND it's an
    // additive click that lands on an already-selected cell, TOGGLE it off.
    const cur = _normalizeSelection(selectionRef.current);
    const s = dragStateRef.current;
    const focus = cur.focus;
    if (s.additive && focus && s.origin.r === focus.r && s.origin.c === focus.c) {
      // detect: was this cell already selected in `initial` before we added
      // the seed range? If yes → user is deselecting.
      const initial = _normalizeSelection(s.initial);
      const alreadyIn = selectionHasCell(initial, focus.r, focus.c);
      if (alreadyIn) {
        // Remove any range/cell that covers this single cell
        const cleaned = _pruneEmpty({
          ranges: initial.ranges.filter(r =>
            !(r.r1 === r.r2 && r.c1 === r.c2 && r.r1 === focus.r && r.c1 === focus.c)),
          cells:  initial.cells.filter(k => k !== cellKey(focus.r, focus.c)),
          cols:   initial.cols,
          anchor: initial.anchor,
          focus,
        });
        emit(cleaned);
      } else {
        // Convert the trailing 1x1 range into a stable single-cell entry.
        const nextRanges = cur.ranges.slice(0, -1);
        const nextCells = Array.from(new Set([...cur.cells, cellKey(focus.r, focus.c)]));
        emit(_pruneEmpty({
          ranges: nextRanges,
          cells:  nextCells,
          cols:   cur.cols,
          anchor: cur.anchor,
          focus,
        }));
      }
    }
    dragStateRef.current = null;
  };
  useEffect(() => {
    const onUp = () => endSelect();
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectColumn = (c, e) => {
    const shift = e.shiftKey;
    const additive = e.ctrlKey || e.metaKey;
    const cur = _normalizeSelection(selectionRef.current);
    if (shift && cur.anchor?.kind === 'col') {
      const [a, b] = [cur.anchor.c, c].sort((x, y) => x - y);
      const cols = [];
      for (let i = a; i <= b; i++) cols.push(i);
      emit({ ranges: [], cells: [], cols,
              anchor: { kind: 'col', c: cur.anchor.c }, focus: null });
      return;
    }
    if (additive) {
      const nextCols = cur.cols.includes(c)
        ? cur.cols.filter(x => x !== c)
        : [...cur.cols, c];
      emit({ ranges: cur.ranges, cells: cur.cells, cols: nextCols,
              anchor: { kind: 'col', c }, focus: null });
      return;
    }
    emit({ ranges: [], cells: [], cols: [c],
            anchor: { kind: 'col', c }, focus: null });
  };

  // ── Inline editors ──────────────────────────────────────────────────
  const [remarkEditing, setRemarkEditing] = useState(null);
  const [remarkDraft, setRemarkDraft] = useState('');
  const commitRemark = (rowRef) => { onRemarkCommit?.(rowRef, remarkDraft); setRemarkEditing(null); };

  const [addedCellEditing, setAddedCellEditing] = useState(null);
  const [addedCellDraft, setAddedCellDraft] = useState('');
  const commitAddedCell = (rowRef, colRef, existingId) => {
    onCellValueCommit?.(rowRef, colRef, addedCellDraft, existingId);
    setAddedCellEditing(null);
  };

  const [sourceEditing, setSourceEditing] = useState(null);
  const [sourceDraft, setSourceDraft] = useState('');
  const commitSourceEdit = (r, c, existingId) => {
    onSourceOverrideCommit?.(rowKey(r), colKey(c), sourceDraft, existingId);
    setSourceEditing(null);
  };

  // ── Rendering ───────────────────────────────────────────────────────
  const totalGridWidth = columnOrder.reduce((s, col) => s + columnWidthOf(col), 0) + (showRemarks ? REMARKS_COL_WIDTH : 0);

  const renderSourceCell = (row, col, colPos) => {
    const r = row.sourceIndex; const c = col.sourceIndex;
    const key = cellKey(r, c);
    if (mergedCoverage.covered.has(key)) return null;
    const merged = mergedCoverage.anchors.get(key);
    const cell = cellByRC.get(key);
    const parserStyle = cell && cell.style_ref >= 0 ? (styles[cell.style_ref] || {}) : {};

    const override = idx.sourceOverrides.get(key);
    const cellHl   = idx.cellStyle.get(key);
    const rowHl    = idx.rowStyle.get(rowKey(r));
    const colHl    = idx.colStyle.get(colKey(c));
    const rangeHl  = rangeHighlightAt(r, c);

    const hl = cellHl?.hl || rangeHl?.hl || rowHl?.hl || colHl?.hl;
    const customFill = cellHl?.custom || rangeHl?.custom || rowHl?.custom || colHl?.custom;
    const bold = cellHl?.bold ?? rangeHl?.bold ?? rowHl?.bold ?? colHl?.bold ?? parserStyle.bold;
    const isSelected = selectionHasCell(selection, r, c);
    // Colour scale only applies when there is no explicit manual highlight/fill.
    const scaleBg = !hl && !customFill ? colorScaleFn(r, c) : null;

    // Effective visible background layer (opaque). Manual highlight > custom
    // > colour scale > parser fill > pure white. The white default is what
    // makes the frozen column safe against scroll bleed-through.
    const explicitFill = (hl && HIGHLIGHT_BG[hl]) || customFill || scaleBg || parserStyle.fill;
    const isFirstColData = colPos === 0;
    const bg = explicitFill || (isFirstColData ? '#FFFFFF' : undefined);

    const numericLike = cell && typeof cell.raw === 'number' && cell.type !== 'd';
    const rawForDisplay = override ? override.value : cell?.raw;
    const displayVal = override
      ? String(override.value ?? '')
      : (cell ? formatCellDisplay(cell.raw, cell.type, parserStyle.number_format, cell.display) : '');
    const alignMap = { left: 'left', center: 'center', right: 'right' };
    const isEditingThis = editable && !presentation && sourceEditing === key;

    // Selection is rendered as an inset outline overlay via ::after so we do
    // NOT tint the background (which would hide fresh highlights) and so
    // discontiguous cells all remain visibly selected.
    return (
      <td key={key}
          rowSpan={merged?.rowspan || 1}
          colSpan={merged?.colspan || 1}
          onMouseDown={(editable || presentation) && !isEditingThis ? (e) => {
            if (e.button !== 0) return;
            // Formula-pick mode: divert click into calc formula, skip selection.
            if (onWorkbookCellClickForFormula && activeCalcCell) {
              const consumed = onWorkbookCellClickForFormula(r, c);
              if (consumed) { e.preventDefault(); e.stopPropagation(); return; }
            }
            beginSelect(r, c, e);
          } : undefined}
          onMouseEnter={(editable || presentation) ? (e) => {
            // Formula-pick range: extend the last inserted ref into a range.
            if (onWorkbookCellClickForFormula && activeCalcCell && (e.buttons & 1)) {
              onWorkbookCellClickForFormula(r, c, true);
              return;
            }
            if (dragStateRef.current) extendSelect(r, c);
          } : undefined}
          onDoubleClick={editable && !presentation ? () => {
            setSourceDraft(String(rawForDisplay ?? ''));
            setSourceEditing(key);
          } : undefined}
          data-testid={`wb-cell-${sheetIdx}-${r}-${c}`}
          data-selected={isSelected ? '1' : '0'}
          className={isSelected ? 'wb-cell-selected' : ''}
          style={{
            width: columnWidthOf(col), minWidth: columnWidthOf(col), maxWidth: columnWidthOf(col),
            padding: compact ? '3px 10px' : '6px 12px',
            fontWeight: bold ? 600 : (r === 1 ? 600 : 400),
            fontStyle: parserStyle.italic ? 'italic' : 'normal',
            textAlign: alignMap[parserStyle.align] || (numericLike ? 'right' : 'left'),
            color: parserStyle.font_color || (override ? '#0d3a26' : '#1A2421'),
            background: bg,
            borderBottom: '1px solid #EEF1EC',
            borderRight: '1px solid #F5F7F4',
            fontSize: compact ? 12 : 13,
            whiteSpace: parserStyle.wrap ? 'normal' : 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontFamily: numericLike ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
            fontVariantNumeric: numericLike ? 'tabular-nums' : 'normal',
            cursor: editable && !presentation ? 'cell' : 'default',
            // TRUE frozen first column
            position: isFirstColData ? 'sticky' : 'relative',
            left: isFirstColData ? 0 : undefined,
            zIndex: isFirstColData ? 4 : undefined,
            // Right shadow on frozen column to visually separate scrolled content
            boxShadow: isFirstColData ? '2px 0 0 rgba(20, 40, 32, 0.04)' : undefined,
            userSelect: 'none',
          }}
          title={override ? `Override · original: ${cell?.display ?? cell?.raw ?? ''}` : (cell?.formula || displayVal || '')}>
        {isEditingThis ? (
          <input autoFocus value={sourceDraft}
                 onChange={(e) => setSourceDraft(e.target.value)}
                 onBlur={() => commitSourceEdit(r, c, override?.id)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter') { e.preventDefault(); commitSourceEdit(r, c, override?.id); }
                   if (e.key === 'Escape') setSourceEditing(null);
                 }}
                 className="w-full outline-none bg-white/90 border border-[color:var(--color-primary)] rounded px-1"
                 style={{ fontFamily: 'inherit', fontSize: 'inherit', textAlign: 'inherit' }}
                 data-testid={`wb-source-edit-${r}-${c}`}/>
        ) : (
          <span>
            {displayVal}
            {override && (
              <span data-testid={`wb-override-dot-${r}-${c}`}
                    style={{ marginLeft: 4, width: 4, height: 4, display: 'inline-block',
                              borderRadius: '50%', background: 'var(--color-primary)',
                              verticalAlign: 'middle' }}
                    title="Edited (source unchanged)"/>
            )}
          </span>
        )}
      </td>
    );
  };

  const renderCalcCell = (row, col) => {
    const r = row.kind === 'source' ? row.sourceIndex : null;
    const cIdx = col.calcIndex;
    if (r == null) {
      // Added rows aren't part of calc math — render blank.
      return (
        <td key={`${row.ref}-QC${cIdx}`}
            data-testid={`wb-calc-blank-${sheetIdx}-${row.ref}-${cIdx}`}
            style={{
              width: columnWidthOf(col), minWidth: columnWidthOf(col),
              background: '#FCFCF8', borderBottom: '1px solid #EEF1EC',
              borderRight: '1px solid #F5F7F4',
            }}/>
      );
    }
    const key = `R${r}C${cIdx}`;
    const cell = calcCells[key];
    const isActive = activeCalcCell?.r === r && activeCalcCell?.c === cIdx;
    return (
      <td key={`${row.ref}-QC${cIdx}`}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            onCalcActivate?.(r, cIdx);
          }}
          data-testid={`wb-calc-${sheetIdx}-R${r}-C${cIdx}`}
          data-active={isActive ? '1' : '0'}
          style={{
            width: columnWidthOf(col), minWidth: columnWidthOf(col), maxWidth: columnWidthOf(col),
            padding: isActive ? 0 : (compact ? '3px 10px' : '6px 12px'),
            fontSize: compact ? 12 : 13,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
            background: isActive ? '#FFFCE8' : '#FBFCF6',
            borderBottom: '1px solid #EEF1EC',
            borderRight: '1px solid #F5F7F4',
            borderLeft: cIdx === 0 ? '2px solid rgba(240, 176, 20, 0.35)' : undefined,
            outline: isActive ? '2px solid var(--color-primary)' : undefined,
            outlineOffset: isActive ? -2 : undefined,
            cursor: 'cell',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
        {isActive ? (
          <input autoFocus value={calcDraft}
                 onChange={(e) => onCalcFormulaChange?.(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter') { e.preventDefault(); onCalcCommit?.(r, cIdx, calcDraft); }
                   if (e.key === 'Escape') { e.preventDefault(); onCalcCommit?.(r, cIdx, cell?.formula ?? ''); }
                 }}
                 data-testid={`wb-calc-input-${r}-${cIdx}`}
                 style={{
                   width: '100%', border: 'none', outline: 'none',
                   background: 'transparent', textAlign: 'right',
                   fontFamily: 'inherit', fontSize: 'inherit',
                   padding: compact ? '3px 10px' : '6px 12px',
                 }}
                 placeholder="=A1+B1"/>
        ) : (
          <span data-testid={`wb-calc-display-${r}-${cIdx}`}
                title={cell?.formula || ''}>{cell?.display ?? ''}</span>
        )}
      </td>
    );
  };

  const renderAddedCellIntoSourceRow = (row, col) => {
    const rowRef = rowKey(row.sourceIndex);
    const colRef = col.ref;
    const combinedRef = `${rowRef}${colRef}`;
    const existing = idx.addedValues.get(combinedRef);
    const isEditing = editable && !presentation && addedCellEditing === combinedRef;
    return (
      <td key={combinedRef}
          onClick={editable && !presentation ? () => {
            if (!isEditing) {
              setAddedCellDraft(String(existing?.value ?? ''));
              setAddedCellEditing(combinedRef);
            }
          } : undefined}
          data-testid={`wb-added-cell-${sheetIdx}-${rowRef}-${colRef}`}
          style={{
            width: columnWidthOf(col), minWidth: columnWidthOf(col), maxWidth: columnWidthOf(col),
            padding: compact ? '3px 10px' : '6px 12px', fontSize: compact ? 12 : 13,
            background: '#FBFDF9',
            borderBottom: '1px solid #EEF1EC', borderRight: '1px solid #F5F7F4',
            borderLeft: '2px solid rgba(45,160,78,0.35)',
            cursor: editable && !presentation ? 'text' : 'default',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
        {isEditing ? (
          <input autoFocus value={addedCellDraft}
                 onChange={(e) => setAddedCellDraft(e.target.value)}
                 onBlur={() => commitAddedCell(rowRef, colRef, existing?.id)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter') { e.preventDefault(); commitAddedCell(rowRef, colRef, existing?.id); }
                   if (e.key === 'Escape') setAddedCellEditing(null);
                 }}
                 className="w-full outline-none bg-transparent"
                 data-testid={`wb-added-input-${rowRef}-${colRef}`}/>
        ) : (existing?.value ?? <span className="text-[#B4C0BB] italic text-[11px]">{editable && !presentation ? 'click to enter' : ''}</span>)}
      </td>
    );
  };

  const renderAddedRow = (row) => {
    return columnOrder.map((col, colPos) => {
      if (col.kind === 'calc') return renderCalcCell(row, col);
      const rowRef = row.ref; const colRef = col.ref;
      const combinedRef = `${rowRef}${colRef}`;
      const existing = idx.addedValues.get(combinedRef);
      const isEditing = editable && !presentation && addedCellEditing === combinedRef;
      const isFirstColData = colPos === 0;
      return (
        <td key={combinedRef}
            onClick={editable && !presentation ? () => {
              if (!isEditing) {
                setAddedCellDraft(String(existing?.value ?? ''));
                setAddedCellEditing(combinedRef);
              }
            } : undefined}
            data-testid={`wb-added-cell-${sheetIdx}-${rowRef}-${colRef}`}
            style={{
              width: columnWidthOf(col), minWidth: columnWidthOf(col), maxWidth: columnWidthOf(col),
              padding: compact ? '3px 10px' : '6px 12px', fontSize: compact ? 12 : 13,
              fontWeight: colPos === 0 ? 600 : 400,
              background: '#F7FAEC',
              borderBottom: '1px solid #E0E9C7', borderRight: '1px solid #EEF1EC',
              borderLeft: colPos === 0 ? '3px solid rgba(240,176,20,0.5)' : undefined,
              cursor: editable && !presentation ? 'text' : 'default',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              position: isFirstColData ? 'sticky' : undefined,
              left: isFirstColData ? 0 : undefined, zIndex: isFirstColData ? 4 : undefined,
              boxShadow: isFirstColData ? '2px 0 0 rgba(20, 40, 32, 0.04)' : undefined,
            }}>
          {isEditing ? (
            <input autoFocus value={addedCellDraft}
                   onChange={(e) => setAddedCellDraft(e.target.value)}
                   onBlur={() => commitAddedCell(rowRef, colRef, existing?.id)}
                   onKeyDown={(e) => {
                     if (e.key === 'Enter') { e.preventDefault(); commitAddedCell(rowRef, colRef, existing?.id); }
                     if (e.key === 'Escape') setAddedCellEditing(null);
                   }}
                   className="w-full outline-none bg-transparent"
                   data-testid={`wb-added-input-${rowRef}-${colRef}`}/>
          ) : (existing?.value ?? <span className="text-[#B4C0BB] italic text-[11px]">{editable && !presentation ? (colPos === 0 ? 'row label' : 'value') : ''}</span>)}
        </td>
      );
    });
  };

  const renderRemarksCell = (row) => {
    const rowRef = row.ref;
    const existing = idx.rowRemark.get(rowRef);
    const rowHl = idx.rowStyle.get(rowRef)?.hl;
    const isEditing = editable && !presentation && remarkEditing === rowRef;
    return (
      <td key={`remark-${rowRef}`}
          data-testid={`wb-remark-${sheetIdx}-${rowRef}`}
          onClick={editable && !presentation && !isEditing ? () => {
            setRemarkDraft(existing?.remark || '');
            setRemarkEditing(rowRef);
          } : undefined}
          style={{
            position: 'sticky', right: 0, zIndex: 6,
            width: REMARKS_COL_WIDTH, minWidth: REMARKS_COL_WIDTH, maxWidth: REMARKS_COL_WIDTH,
            padding: compact ? '3px 10px' : '6px 12px',
            fontSize: compact ? 12 : 12.5,
            background: rowHl ? HIGHLIGHT_BG[rowHl] : (row.kind === 'added' ? '#F7FAEC' : '#FDFDFC'),
            borderBottom: '1px solid #EEF1EC',
            borderLeft: '2px solid var(--color-primary)',
            color: '#1A2421', verticalAlign: 'middle',
            whiteSpace: 'normal', wordBreak: 'break-word',
            cursor: editable && !presentation ? 'text' : 'default',
          }}>
        {isEditing ? (
          <textarea autoFocus rows={2}
                    value={remarkDraft}
                    onChange={(e) => setRemarkDraft(e.target.value)}
                    onBlur={() => commitRemark(rowRef)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setRemarkEditing(null);
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commitRemark(rowRef); }
                    }}
                    className="w-full outline-none bg-white/70 rounded border border-[color:var(--color-primary)]/30 px-1 py-0.5 resize-none"
                    data-testid={`wb-remark-input-${rowRef}`}/>
        ) : (existing?.remark || (editable && !presentation
          ? <span className="text-[#B4C0BB] italic text-[11px]">click to add remark</span>
          : ''))}
      </td>
    );
  };

  return (
    <div className="wb-renderer flex flex-col h-full min-h-0 bg-white" data-testid={`workbook-renderer-${sheetIdx}`}>
      <div ref={scrollRef} className="flex-1 overflow-auto" style={{ background: '#FFFFFF' }}>
        <table className="wb-table" style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed',
                                              width: totalGridWidth, background: '#FFFFFF' }}>
          <colgroup>
            {columnOrder.map(col => <col key={`cg-${col.ref}`} style={{ width: columnWidthOf(col) }}/>)}
            {showRemarks && <col style={{ width: REMARKS_COL_WIDTH }}/>}
          </colgroup>
          <thead>
            <tr>
              {columnOrder.map((col, colPos) => {
                const isFirstColData = colPos === 0;
                const isCalc = col.kind === 'calc';
                const label = isCalc ? col.title : (col.kind === 'added' ? (col.title || 'Column') : '');
                const colSelected = col.kind === 'source' && selection?.cols?.includes(col.sourceIndex);
                return (
                  <th key={`h-${col.ref}`}
                      data-testid={`wb-colheader-${col.ref}`}
                      onMouseDown={col.kind === 'source' && editable && !presentation
                                ? (e) => selectColumn(col.sourceIndex, e) : undefined}
                      style={{
                        position: 'sticky', top: 0, zIndex: isFirstColData ? 20 : 10,
                        left: isFirstColData ? 0 : undefined,
                        width: columnWidthOf(col), minWidth: columnWidthOf(col), maxWidth: columnWidthOf(col),
                        background: colSelected
                          ? 'var(--color-primary-tint)'
                          : (isCalc ? '#FFF9E4' : (col.kind === 'added' ? 'var(--color-primary-tint)' : '#F9FBF7')),
                        borderBottom: colSelected
                          ? '2px solid var(--color-primary)'
                          : (isCalc ? '2px solid #F0B014' : '1px solid #E7EBE6'),
                        borderRight: '1px solid #F0F4EE',
                        borderLeft: isCalc && col.calcIndex === 0 ? '2px solid rgba(240, 176, 20, 0.5)' : undefined,
                        padding: 0, height: isCalc || col.kind === 'added' ? 32 : 14,
                        textAlign: isCalc ? 'center' : 'left', fontSize: 11, fontWeight: 700,
                        color: isCalc ? '#7A5000' : 'var(--color-primary-hover)', textTransform: 'uppercase',
                        letterSpacing: '0.05em', userSelect: 'none',
                        boxShadow: isFirstColData ? '2px 0 0 rgba(20, 40, 32, 0.04)' : undefined,
                        cursor: col.kind === 'source' && editable && !presentation ? 'pointer' : 'default',
                      }}>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center',
                                   paddingLeft: 12, height: '100%' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                      <div onMouseDown={editable && !presentation ? (e) => onResizeMouseDown(col, e) : undefined}
                           onDoubleClick={editable && !presentation ? () => autoFitColumn(col) : undefined}
                           data-testid={`wb-resize-${col.ref}`}
                           title="Drag to resize · double-click to auto-fit"
                           style={{
                             position: 'absolute', top: 0, right: -6, width: 12, height: '100%',
                             cursor: editable && !presentation ? 'col-resize' : 'default',
                             zIndex: 30,
                             borderRight: '2px solid rgba(28, 71, 56, 0.35)',
                             background: pendingWidth[col.ref] != null ? 'var(--color-primary)' : 'transparent',
                             opacity: pendingWidth[col.ref] != null ? 0.6 : 0,
                             transition: 'opacity 120ms ease',
                           }}
                           className={editable && !presentation ? 'wb-resize-handle' : ''}/>
                    </div>
                  </th>
                );
              })}
              {showRemarks && (
                <th data-testid="wb-remarks-header"
                    style={{
                      position: 'sticky', top: 0, right: 0, zIndex: 21,
                      width: REMARKS_COL_WIDTH, minWidth: REMARKS_COL_WIDTH,
                      background: 'var(--color-primary-tint)',
                      borderBottom: '1px solid var(--color-primary)',
                      borderLeft: '2px solid var(--color-primary)',
                      padding: '6px 12px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700,
                      color: 'var(--color-primary-hover)', textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}>Remarks</th>
              )}
            </tr>
          </thead>
          <tbody>
            {needsVirt && firstRowIdx > 0 && (
              <tr><td colSpan={columnOrder.length + (showRemarks ? 1 : 0)}
                       style={{ height: rowOffsets[firstRowIdx] }}/></tr>
            )}
            {rowOrder.slice(firstRowIdx, lastRowIdx + 1).map((row) => {
              const h = rowHeightOf(row);
              if (h === 0) return null;
              const rowStyleHint = idx.rowStyle.get(row.ref);
              const rowBg = row.kind === 'added'
                ? (rowStyleHint?.custom || '#F7FAEC')
                : (rowStyleHint?.hl ? HIGHLIGHT_BG[rowStyleHint.hl] : (rowStyleHint?.custom || undefined));
              return (
                <tr key={row.ref}
                    data-testid={`wb-row-${sheetIdx}-${row.ref}`}
                    style={{ height: h, background: rowBg }}>
                  {row.kind === 'source' ? (
                    columnOrder.map((col, colPos) => {
                      if (col.kind === 'source') return renderSourceCell(row, col, colPos);
                      if (col.kind === 'calc')   return renderCalcCell(row, col);
                      return renderAddedCellIntoSourceRow(row, col);
                    })
                  ) : renderAddedRow(row)}
                  {showRemarks && renderRemarksCell(row)}
                </tr>
              );
            })}
            {needsVirt && lastRowIdx < rowOrder.length - 1 && (
              <tr><td colSpan={columnOrder.length + (showRemarks ? 1 : 0)}
                       style={{ height: totalHeight - rowOffsets[lastRowIdx + 1] }}/></tr>
            )}
          </tbody>
        </table>
      </div>
      <style>{`
        .wb-resize-handle:hover { opacity: 0.55 !important; background: var(--color-primary) !important; }
        .wb-cell-selected { outline: 2px solid var(--color-primary); outline-offset: -2px; }
      `}</style>
    </div>
  );
}

function _boxFrom(a, b) {
  return {
    r1: Math.min(a.r, b.r), c1: Math.min(a.c, b.c),
    r2: Math.max(a.r, b.r), c2: Math.max(a.c, b.c),
  };
}

// ── Colour-scale interpolation helper ─────────────────────────────────
function _scaleColorAt(pct, palette, direction = 'high_good') {
  // Two-stop palettes: 'gyr' (green→yellow→red is the reverse — we invert so
  // low=red, mid=yellow, high=green, i.e. positive-good).
  //   pct in [0, 1]. Colour space in RGB, midpoint interpolation.
  //
  // sprint 376 · direction reversal (Low is Good / High is Good):
  //   • high_good (default) — low = red, mid = amber, high = green.
  //   • low_good            — low = green, mid = amber, high = red.
  //   Achieved by flipping pct — no separate palette needed.
  let clamp = Math.max(0, Math.min(1, pct));
  if (direction === 'low_good') clamp = 1 - clamp;
  // Positive-good palette (default 'gyr'): low = red, mid = amber, high = green.
  const stops = palette === 'blue'
    ? [[220, 235, 245], [149, 195, 232], [40, 116, 187]]
    : [[249, 205, 199], [252, 231, 176], [193, 232, 197]];  // red → amber → green
  const [a, b] = clamp < 0.5 ? [stops[0], stops[1]] : [stops[1], stops[2]];
  const t = clamp < 0.5 ? clamp * 2 : (clamp - 0.5) * 2;
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}
